import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { logError } from "../../../../../../../service/error";
import { updateStandardSpecificData } from "../../../../../../../service/project";
import { GetProjectDetailsResponse } from "../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import { useIsLoadingWrapper } from "../../../../../../../utils";
import { Toast } from "../../../../../../../widget";
import { ProjectContext } from "../../../ProjectContext";
import { DataType, StandardSpecificData, Step } from "./types";
import {
  applyComponentDefaultValues,
  createExpressionContext,
  refreshDependentDefaultValues,
  setStandardSpecificValue,
} from "./utils";

interface useStandardSpecificSectionReturnData {
  data: StandardSpecificData;
  errors: { [key: string]: ServiceError[] };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expressionContext: any;
  onSave: () => void;
  onCancel: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateData: (dataPath: string, value: any, dataType: DataType) => void;
  isOnSaveLoading: boolean;
  isReadOnly: boolean;
  setIsReadOnly: (value: boolean) => void;
}

/**
 * @param steps the schema's steps - needed on save to write each empty field's defaultValueExpression
 * into the data, so the defaults the section displays are also the ones it stores.
 */
export const useStandardSpecificSection = (steps: Step[] | undefined): useStandardSpecificSectionReturnData => {
  const { projectDetails, publicProjectDetails, setProjectDetails } = useContext(ProjectContext);
  const [data, setData] = useState<StandardSpecificData>(
    projectDetails?.standardSpecificData ?? publicProjectDetails?.standardSpecificData ?? {}
  );
  const [submittedData, setSubmittedData] = useState<StandardSpecificData>(
    projectDetails?.standardSpecificData ?? publicProjectDetails?.standardSpecificData ?? {}
  );
  const [errors, setErrors] = useState<{ [key: string]: ServiceError[] }>({});
  const [isOnSaveLoading, setIsOnSaveLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);

  const updateData = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dataPath: string, value: any, dataType: DataType): void => {
      // Fields computed from this one (e.g. unit capacity from area) follow the change.
      setData(
        refreshDependentDefaultValues(
          steps,
          data,
          setStandardSpecificValue(data, dataPath, value, dataType),
          projectDetails
        )
      );
    },
    [data, steps, projectDetails]
  );

  useEffect(() => {
    setData(projectDetails?.standardSpecificData || publicProjectDetails?.standardSpecificData || {});
  }, [projectDetails, publicProjectDetails]);

  const processErrors = (serviceErrors?: ServiceError[]): void => {
    data.steps?.forEach((step) => {
      const stepErrors = serviceErrors?.filter((error) => error.path?.split(".")[0] === step.key) ?? [];
      if (stepErrors.length > 0) {
        setErrors((prevErrors) => ({ ...prevErrors, [step.key]: stepErrors }));
      }
    });

    const firstStepKey = data.steps?.[0].key;
    // for errors without a path, set the error on the first step without overriding existing errors
    const otherErrors = serviceErrors?.filter((error) => !error.path) ?? [];
    if (otherErrors.length > 0 && firstStepKey) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [firstStepKey]: [...(prevErrors[firstStepKey] ?? []), ...otherErrors],
      }));
    }
  };

  const onSave = useIsLoadingWrapper(async (): Promise<void> => {
    setErrors({});

    // Store the defaults the section is displaying for any field still left empty.
    const { data: dataToSave, errors: defaultValueErrors } = applyComponentDefaultValues(steps, data, projectDetails);
    // A broken expression only loses its default; report it rather than blocking the save.
    defaultValueErrors.forEach((error) => logError({ error }));

    await updateStandardSpecificData({
      privateStandardSpecificData: projectDetails?.privateStandardSpecificData ?? null,
      projectUuid: projectDetails?.uuid ?? "",
      rowVersion: projectDetails?.rowVersion ?? 0,
      standardSpecificData: dataToSave,
      standardSpecificSchemaUuid: projectDetails?.standardSpecificSchemaUuid ?? null,
    }).then((res) => {
      if (res.status === Status.Success && res.data) {
        Toast.success({ message: "Standard specific details updated successfully" });
        setData(dataToSave);
        setSubmittedData(dataToSave);
        setProjectDetails({
          ...(projectDetails as GetProjectDetailsResponse),
          standardSpecificData: dataToSave,
          rowVersion: res.data.rowVersion,
        });
        setIsReadOnly(true);
      } else if (res.status === Status.Error) {
        processErrors(res.errors);
      }
    });
  }, setIsOnSaveLoading);

  const onCancel = (): void => {
    setData(submittedData);
    setIsReadOnly(true);
  };

  const refreshExpressionContext = data && projectDetails !== undefined;
  const expressionContext = useMemo(
    () => createExpressionContext(data, projectDetails),
    [refreshExpressionContext, data]
  );

  return {
    isReadOnly,
    setIsReadOnly,
    data,
    errors,
    expressionContext,
    onSave,
    onCancel,
    updateData,
    isOnSaveLoading,
  };
};
